import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Trophy, Crown, Medal, Star, Users, GraduationCap, Loader2, Building2 } from 'lucide-react';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: 'school', label: 'My School', icon: Building2 },
  { id: 'global', label: 'Global', icon: Trophy },
  { id: 'schools', label: 'Top Schools', icon: GraduationCap },
];

const LeaderboardsPage = () => {
  const [tab, setTab] = useState('school');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/leaderboards/${tab}`);
        setData(res.data);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    fetch();
  }, [tab]);

  const getRankIcon = (i) => {
    if (i === 0) return <Crown className="w-5 h-5 text-yellow-400" />;
    if (i === 1) return <Medal className="w-5 h-5 text-gray-300" />;
    if (i === 2) return <Medal className="w-5 h-5 text-amber-600" />;
    return <span className="w-5 h-5 flex items-center justify-center text-xs text-[var(--isp-muted)] font-bold">{i + 1}</span>;
  };

  return (
    <div className="f500-page max-w-3xl mx-auto space-y-6" data-testid="leaderboards-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold text-[var(--isp-heading)] flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center">
            <Trophy className="w-5 h-5 text-white" />
          </div>
          Leaderboards
        </h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-2" data-testid="leaderboard-tabs">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-all ${tab === t.id ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30' : 'text-[var(--isp-body)] hover:bg-[var(--isp-glass-inner)]'}`}
            data-testid={`lb-tab-${t.id}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-yellow-500" /></div>
      ) : tab === 'schools' ? (
        <Card className=" bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)] overflow-hidden" data-testid="schools-list">
          <div className="p-5">
            <h2 className="text-lg font-semibold text-[var(--isp-heading)] mb-4">Top Institutions</h2>
            {(data?.schools || []).length === 0 ? (
              <p className="text-sm text-[var(--isp-muted)] text-center py-8">No schools yet. Update your institution in your profile!</p>
            ) : (
              <div className="space-y-2">
                {data.schools.map((s, i) => (
                  <div key={i} className={`flex items-center gap-3 p-3 rounded-xl ${s.is_user_school ? 'bg-yellow-500/10 border border-yellow-500/20' : 'bg-white/[0.02] hover:bg-white/[0.04]'}`}>
                    {getRankIcon(i)}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[var(--isp-heading)] truncate">{s.name}</p>
                      <p className="text-[10px] text-[var(--isp-muted)]">{s.student_count} students</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-yellow-400">{(s.total_xp || 0).toLocaleString()} XP</p>
                      <p className="text-[10px] text-[var(--isp-muted)]">avg {s.avg_xp} XP</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      ) : (
        <>
          {/* User rank banner */}
          {data?.user_rank && (
            <Card className="p-4  bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-yellow-500/20" data-testid="user-rank">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                  <Star className="w-6 h-6 text-yellow-400" />
                </div>
                <div>
                  <p className="text-sm text-[var(--isp-body)]">Your Rank</p>
                  <p className="text-2xl font-bold text-yellow-400">#{data.user_rank}</p>
                </div>
                {data.institution && <p className="ml-auto text-sm text-[var(--isp-muted)]">{data.institution}</p>}
              </div>
            </Card>
          )}

          {/* Leaderboard list */}
          <Card className=" bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)] overflow-hidden" data-testid="leaderboard-list">
            <div className="p-5 space-y-2">
              {(data?.leaderboard || []).length === 0 ? (
                <p className="text-sm text-[var(--isp-muted)] text-center py-8">{data?.message || 'No entries yet.'}</p>
              ) : data.leaderboard.map((e, i) => (
                <motion.div key={e.user_id || i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }}
                  className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${e.is_self ? 'bg-purple-500/10 border border-purple-500/20' : 'bg-white/[0.02] hover:bg-white/[0.04]'}`}
                  data-testid={`lb-entry-${i}`}>
                  {getRankIcon(i)}
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                    {(e.name || 'S')[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[var(--isp-heading)] truncate">{e.name} {e.is_self && <span className="text-[10px] text-purple-300">(you)</span>}</p>
                    <p className="text-[10px] text-[var(--isp-muted)]">Lv.{e.level} {e.level_name} · {e.badge_count || 0} badges{e.institution ? ` · ${e.institution}` : ''}</p>
                  </div>
                  <p className="text-sm font-bold text-yellow-400">{(e.xp || 0).toLocaleString()} XP</p>
                </motion.div>
              ))}
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

export default LeaderboardsPage;
